import { Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { Observable, Subject, Subscription, Subscriber, interval, timer, throwError } from 'rxjs';
import { retry, map } from 'rxjs/operators';
import { webSocket, WebSocketSubjectConfig, WebSocketSubject } from 'rxjs/webSocket';

import { WINDOW_TOKEN } from '../window.token';
import { RestJsonCodecService } from '../rest/rest-json-codec.service';
import {
  WebsocketMessage,
  WebsocketConfiguration,
  WebsocketData,
  WebsocketErrorStatusCodes
} from './websocket.domain';

/**
 * Websocket Service creates websocket connection and emit data as message Observable
 * Will try to connect until session timout if failed
 *
 * onMessageReceived will emit all received messages without any filter
 * Subscribe to onMessageReceived and filter base on type (WebsocketMessageType)
 *
 */

export const WEBSOCKET_CONFIGURATION = new InjectionToken<WebsocketConfiguration>('websocketConfiguration');

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private readonly websocketConfiguration = inject<WebsocketConfiguration>(WEBSOCKET_CONFIGURATION, { optional: true });
  private readonly browserWindow = inject<Window>(WINDOW_TOKEN);
  private readonly restJsonCodecService = inject(RestJsonCodecService);
  private readonly zone = inject(NgZone);

  private static readonly DEFAULT_DELAY: number = 10000;
  private static readonly HEARTBEAT_MESSAGE: string = 'HEARTBEAT';

  readonly onMessageReceived: Subject<WebsocketMessage<WebsocketData>>;

  private socketSubscription: Subscription = Subscription.EMPTY;
  private connectSubscription: Subscription = Subscription.EMPTY;
  private heartbeatSubscription: Subscription = Subscription.EMPTY;
  private sessionTimeout: number;
  private socketSubject: WebSocketSubject<WebsocketData | string>;

  constructor() {
    this.onMessageReceived = new Subject<WebsocketMessage<WebsocketData>>();
  }

  /**
   * Web socket connection status
   *
   * @returns true when connected
   */
  isConnected(): boolean {
    return !this.connectSubscription.closed;
  }

  /**
   * Connect to websocket try if fails using delay
   */
  connect(): void {
    if (this.connectSubscription.closed) {
      const connectObservable: Observable<WebsocketMessage<WebsocketData>> = new Observable<WebsocketMessage<WebsocketData>>(
        (subscriber: Subscriber<string | WebsocketData>): (() => void) => {
          try {
            this.socketUnsubscribe();
            if (this.hasValidConfiguration() && this.websocketConfiguration.url !== undefined) {
              const url = this.getWebsocketUrl();
              const socketConfig: WebSocketSubjectConfig<WebsocketData | string> = {
                url,
                openObserver: {
                  next: (): void => {
                    this.zone.runOutsideAngular((): void => {
                      this.heartbeatSubscription = interval(WebsocketService.DEFAULT_DELAY).subscribe((): void => {
                        this.socketSubject.next(WebsocketService.HEARTBEAT_MESSAGE);
                      });
                    });
                  }
                },
                closeObserver: {
                  next: (event: CloseEvent): void => {
                    this.attemptToReconnect(subscriber, event);
                  }
                }
              };

              this.socketSubject = webSocket(socketConfig);
              this.socketSubscription = this.socketSubject.subscribe({
                next: (data: string | WebsocketData): void => subscriber.next(data),
                error: (): void => this.heartbeatSubscription.unsubscribe()
              });
            }

            return ((): void => {
              this.socketUnsubscribe();
            });
          } catch (error: unknown) {
            this.attemptToReconnect(subscriber, error);
          }
        }).pipe(
        retry({
          delay: (error: CloseEvent, retryCount: number): Observable<unknown> => {
            if (retryCount * WebsocketService.DEFAULT_DELAY <= this.sessionTimeout) {
              return timer(WebsocketService.DEFAULT_DELAY);
            }
            return throwError(() => error);
          }
        }),
        map((data: WebsocketData): WebsocketMessage<WebsocketData> => this.decode(data))
      );

      this.connectSubscription = connectObservable
        .subscribe((data: WebsocketMessage<WebsocketData>): void => {
          this.onMessageReceived.next(data);
        });
    }
  }

  /**
   * Disconnect websocket
   */
  disconnect(): void {
    if (!this.connectSubscription.closed) {
      this.connectSubscription.unsubscribe();
    }
  }

  sendData(data: WebsocketData): void {
    if (this.socketSubject !== undefined) {
      this.socketSubject.next(data);
    }
  }

  private hasValidConfiguration(): boolean {
    return this.websocketConfiguration !== null && this.websocketConfiguration !== undefined;
  }

  /**
   * Unsubscribe socket
   */
  private socketUnsubscribe(): void {
    if (!this.heartbeatSubscription.closed) {
      this.heartbeatSubscription.unsubscribe();
    }
    if (!this.socketSubscription.closed) {
      this.socketSubscription.unsubscribe();
    }
  }

  /**
   * Get url + connectid if in query string
   */
  private getWebsocketUrl(): string {
    let url: string = this.websocketConfiguration.url;
    const search: string = this.browserWindow.location.search;
    if (search !== undefined && search !== '') {
      const matcher = /connectId=([0-9-A-Z]+).*/;
      const match = search.match(matcher);
      if (match !== undefined && match !== null) {
        url = url + search;
      }
    }
    return url;
  }

  /**
   * Decode websocket response
   *
   * @param
   * @returns
   */
  private decode(data: WebsocketData): WebsocketMessage<WebsocketData> {
    return this.restJsonCodecService.decode(JSON.stringify(data));
  }

  /**
   * Try to reconnect based on event code and session timeout
   *
   * @param
   */
  private attemptToReconnect(subscriber: Subscriber<WebsocketData>, event: unknown): void {
    this.heartbeatSubscription.unsubscribe();

    const closeEvent = event as CloseEvent;

    if (closeEvent !== undefined && closeEvent.code !== undefined &&
      Object.keys(WebsocketErrorStatusCodes).find((code: string): boolean => code === String(closeEvent.code)) !== undefined
    ) {
      subscriber.complete();
    } else if (this.hasValidConfiguration() && this.websocketConfiguration.sessionTimeoutCallback !== undefined ) {
      this.websocketConfiguration.sessionTimeoutCallback().subscribe((timeout: number): void => {
        // session timout is tracked in seconds, not milliseconds
        this.sessionTimeout = timeout * 1000;
        subscriber.error(closeEvent);
      });
    } else {
      subscriber.complete();
    }
  }
}

